import {ShareableStaticGraph} from '../../../shared/navigation/SharedGraph';
import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
import React, { useEffect, useMemo, useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { CommonTable } from "../../../shared/ui/table/CommonTable";
import { PieChartWithToggles } from "../../../shared/ui/chart/PieChartWithToggles.jsx";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import { getChartColor } from "../../../shared/constants/chartColors";
import { HoverPopover } from "../../../shared/ui/overlay/HoverPopover.jsx";
import { toJSDate, formatMMDDYYYY } from "../utils/componentsUtils";
import { loadSeecDocuments } from "../utils/loadSeecDocuments";
import { db } from "../../../firebase";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  Legend,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Title,
  Legend,
  zoomPlugin,
  Filler
);

function normalizeDonorName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function chunkValues(values, size = 10) {
  const chunks = [];

  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }

  return chunks;
}

function UnifiedContributionSection({
  collapsed,
  toggle,
  title = "Total Receipts",
  docIds = ["GDTC-Contributions"], // Can be one or many
  rows = null,
  color = "#e0f2fe",
  showRawTable = true,
}) {
  const [allReceiptData, setAllReceiptData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [topContributors, setTopContributors] = useState([]);
  const [showTable, setShowTable] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:showTable:" + [title].join(":"), false);
  const [showTop, setShowTop] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:showTop:" + [title].join(":"), false);
  const [showChart, setShowChart] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:showChart:" + [title].join(":"), false);
  const [showReceiptStatsChart, setShowReceiptStatsChart] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:showReceiptStatsChart:" + [title].join(":"), false);
  const [showTopShareChart, setShowTopShareChart] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:showTopShareChart:" + [title].join(":"), false);
  const [showMethodPieChart, setShowMethodPieChart] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:showMethodPieChart:" + [title].join(":"), false);
  const [showOccupationPieChart, setShowOccupationPieChart] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:showOccupationPieChart:" + [title].join(":"), false);
  const [yearWindow, setYearWindow] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:yearWindow:" + [title].join(":"), "5");
  const [rawYearWindow, setRawYearWindow] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:rawYearWindow:" + [title].join(":"), "5");
  const [chartYearWindow, setChartYearWindow] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:chartYearWindow:" + [title].join(":"), "5");
  const [receiptStatsYearWindow, setReceiptStatsYearWindow] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:receiptStatsYearWindow:" + [title].join(":"), "5");
  const [topShareCount, setTopShareCount] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:topShareCount:" + [title].join(":"), "25");
  const [methodYearWindow, setMethodYearWindow] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:methodYearWindow:" + [title].join(":"), "5");
  const [occupationYearWindow, setOccupationYearWindow] = useSharedFilter("UnifiedContributionSection.jsx:UnifiedContributionSection:occupationYearWindow:" + [title].join(":"), "5");
  const [donorLookup, setDonorLookup] = useState(new Map());
  const yearWindowOptions = ["ALL", "1", "3", "5", "10", "15"];
  const topCountOptions = ["5", "10", "15", "25", "50", "100"];

  function filterRowsByYearWindow(entries, windowValue) {
    const validYears = entries
      .map((entry) => entry.__parsedDate)
      .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
      .map((date) => date.getFullYear())
      .filter((year) => year > 1970);
    const latestYear = validYears.length ? Math.max(...validYears) : null;
    const minYear =
      windowValue === "ALL" || latestYear === null
        ? null
        : latestYear - Number(windowValue) + 1;

    return minYear === null
      ? entries
      : entries.filter((entry) => entry.__parsedDate.getFullYear() >= minYear);
  }

  function extractHexFromTailwind(bgClass = "") {
    const match = bgClass.match(/#([0-9a-fA-F]{6})/);
    const hex = match ? match[1] : "38bdf8";
    const amt = -20;

    const darken = (component) => {
      const dec = parseInt(component, 16);
      return Math.max(0, dec + amt)
        .toString(16)
        .padStart(2, "0");
    };

    const r = darken(hex.slice(0, 2));
    const g = darken(hex.slice(2, 4));
    const b = darken(hex.slice(4, 6));
    return `#${r}${g}${b}`;
  }

  function getDonorChartData(history) {
    return history
      .slice()
      .sort((a, b) => a.__parsedDate - b.__parsedDate)
      .map((entry) => ({
        date: formatMMDDYYYY(entry.__parsedDate),
        total: entry["Contribution Amount (Sort)"],
      }));
  }

  function buildContributorTotals(entries) {
    const totals = new Map();

    for (const entry of entries) {
      const name = entry["Full Name"]?.trim();
      const amount = entry["Contribution Amount (Sort)"];
      const key = name?.toLowerCase();

      if (!key) continue;
      if (!totals.has(key)) {
        totals.set(key, { Name: name, rawTotal: 0 });
      }
      totals.get(key).rawTotal += amount;
    }

    return Array.from(totals.values()).sort((a, b) => b.rawTotal - a.rawTotal);
  }

  function normalizeOccupationLabel(value) {
    const raw = String(value || "").trim();
    if (!raw) return "Unknown";

    const normalized = raw
      .toLowerCase()
      .replace(/[.,]/g, "")
      .replace(/\s+/g, " ")
      .trim();

    if (
      [
        "unknown",
        "none",
        "n/a",
        "na",
        "not employed",
        "unemployed",
        "homemaker",
      ].includes(normalized)
    ) {
      return "Unknown";
    }

    if (normalized.includes("retired")) return "Retired";
    if (normalized.includes("teacher") || normalized.includes("educator"))
      return "Education";
    if (
      normalized.includes("attorney") ||
      normalized.includes("lawyer") ||
      normalized.includes("legal")
    ) {
      return "Legal";
    }
    if (
      normalized.includes("manager") ||
      normalized === "mgr" ||
      normalized.includes("management")
    ) {
      return "Manager";
    }
    if (
      normalized.includes("owner") ||
      normalized.includes("self employed") ||
      normalized === "self"
    ) {
      return "Business Owner";
    }
    if (
      normalized.includes("account") ||
      normalized.includes("finance") ||
      normalized.includes("cpa") ||
      normalized.includes("auditor")
    ) {
      return "Finance";
    }
    if (
      normalized.includes("engineer") ||
      normalized.includes("programmer") ||
      normalized.includes("developer") ||
      normalized.includes("it ")
    ) {
      return "Technology";
    }
    if (
      normalized.includes("doctor") ||
      normalized.includes("nurse") ||
      normalized.includes("medical") ||
      normalized.includes("health") ||
      normalized.includes("psychologist")
    ) {
      return "Healthcare";
    }
    if (
      normalized.includes("student") ||
      normalized.includes("professor") ||
      normalized.includes("teacher")
    ) {
      return "Education";
    }

    return raw
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }

  useEffect(() => {
    async function fetchData() {
      const allRows = rows
        ? rows
        : (await loadSeecDocuments(docIds)).flatMap((loadedDoc) => loadedDoc.data || []);

      const cleaned = allRows
        .map((entry) => {
          const amountStr = entry["Contribution Amount"];
          const amountNum =
            typeof amountStr === "number"
              ? amountStr
              : parseFloat(amountStr?.replace(/[^\d.-]/g, "")) || 0;

          const parsedDate = toJSDate(entry["Date"]);

          return {
            ...entry,
            "Contribution Amount (Sort)": amountNum,
            __parsedDate: parsedDate ?? new Date(0), // always sortable
            __dateDisplay: formatMMDDYYYY(parsedDate),
          };
        })
        .sort((a, b) => b.__parsedDate - a.__parsedDate);

      setAllReceiptData(cleaned);
      setRawData(filterRowsByYearWindow(cleaned, rawYearWindow));

      const topSourceRows = filterRowsByYearWindow(cleaned, yearWindow);

      const aggregationMap = {};
      for (const entry of topSourceRows) {
        const name = entry["Full Name"]?.trim();
        const amount = entry["Contribution Amount (Sort)"];
        const key = name?.toLowerCase();

        // Skip rows with no usable name to avoid collapsing everything into "undefined"
        if (!key) continue;

        if (!aggregationMap[key]) {
          aggregationMap[key] = {
            name,
            total: 0,
            count: 0,
            max: 0,
            earliestDate: entry.__parsedDate,
            latestDate: entry.__parsedDate,
            donorDocId: entry.DonorDocId || null,
          };
        }

        if (entry.__parsedDate < aggregationMap[key].earliestDate) {
          aggregationMap[key].earliestDate = entry.__parsedDate;
        }

        if (entry.__parsedDate > aggregationMap[key].latestDate) {
          aggregationMap[key].latestDate = entry.__parsedDate;
        }

        aggregationMap[key].total += amount;
        aggregationMap[key].count += 1;
        aggregationMap[key].max = Math.max(aggregationMap[key].max, amount);
      }

      const result = Object.values(aggregationMap)
        .map((entry) => {
          const avg = entry.count ? Math.floor(entry.total / entry.count) : 0;
          return {
            Name: entry.name,
            "First Receipt":
              entry.earliestDate instanceof Date &&
              !Number.isNaN(entry.earliestDate.getTime())
                ? formatMMDDYYYY(entry.earliestDate)
                : "—",
            "First Receipt (Sort)":
              entry.earliestDate instanceof Date &&
              !Number.isNaN(entry.earliestDate.getTime())
                ? entry.earliestDate.getTime()
                : 0,
            "Last Receipt":
              entry.latestDate instanceof Date &&
              !Number.isNaN(entry.latestDate.getTime())
                ? formatMMDDYYYY(entry.latestDate)
                : "—",
            "Last Receipt (Sort)":
              entry.latestDate instanceof Date &&
              !Number.isNaN(entry.latestDate.getTime())
                ? entry.latestDate.getTime()
                : 0,
            "Total Receipts": `$${entry.total.toLocaleString()}`,
            "Total Receipts (Sort)": entry.total,
            "Number of Receipts": entry.count,
            "Average Receipt": avg,
            "Average Receipt (Sort)": avg,
            "Max Receipt": entry.max,
            "Max Receipt (Sort)": entry.max,
            rawTotal: entry.total,
            DonorDocId: entry.donorDocId || null,
          };
        })
        .sort((a, b) => b.rawTotal - a.rawTotal);

      setTopContributors(result);
    }

    fetchData();
  }, [docIds, rows, yearWindow, rawYearWindow]);

  useEffect(() => {
    let isMounted = true;

    async function loadDonorLookup() {
      if (collapsed || !allReceiptData.length) {
        if (isMounted) setDonorLookup(new Map());
        return;
      }

      const normalizedNames = Array.from(
        new Set(
          allReceiptData
            .map((entry) => normalizeDonorName(entry["Full Name"]))
            .filter(Boolean),
        ),
      );

      if (!normalizedNames.length) {
        if (isMounted) setDonorLookup(new Map());
        return;
      }

      const nextLookup = new Map();
      const chunks = chunkValues(normalizedNames, 10);

      for (const chunk of chunks) {
        const snap = await getDocs(
          query(collection(db, "SEEC_DONORS"), where("normalizedName", "in", chunk)),
        );

        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const normalizedName = normalizeDonorName(data.normalizedName);
          if (normalizedName && !nextLookup.has(normalizedName)) {
            nextLookup.set(normalizedName, docSnap.id);
          }
        });
      }

      if (isMounted) {
        setDonorLookup(nextLookup);
      }
    }

    loadDonorLookup();

    return () => {
      isMounted = false;
    };
  }, [collapsed, allReceiptData]);

  const linkedAllReceiptData = useMemo(
    () =>
      allReceiptData.map((entry) => ({
        ...entry,
        DonorDocId:
          entry.DonorDocId ||
          donorLookup.get(normalizeDonorName(entry["Full Name"])) ||
          null,
      })),
    [allReceiptData, donorLookup],
  );

  const linkedRawData = useMemo(
    () =>
      rawData.map((entry) => ({
        ...entry,
        DonorDocId:
          entry.DonorDocId ||
          donorLookup.get(normalizeDonorName(entry["Full Name"])) ||
          null,
      })),
    [rawData, donorLookup],
  );

  const linkedTopContributors = useMemo(
    () =>
      topContributors.map((entry) => ({
        ...entry,
        DonorDocId:
          entry.DonorDocId ||
          donorLookup.get(normalizeDonorName(entry.Name)) ||
          null,
      })),
    [topContributors, donorLookup],
  );

  const rawColumns = [
    {
      key: "Full Name",
      label: "Name",
      style: { width: "200px" },
      render: (row) =>
        row.DonorDocId ? (
          <Link
            to={`/seec/donor/${row.DonorDocId}`}
            className="text-blue-700 hover:text-blue-900 underline"
          >
            {row["Full Name"]}
          </Link>
        ) : (
          row["Full Name"]
        ),
    },
    { key: "Use", label: "Method of Payment", style: { width: "180px" } },
    {
      key: "Date",
      label: "Receipt Date",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
      sortKey: "__parsedDate",
      render: (row) => row.__dateDisplay ?? "—",
    },
    {
      key: "Contribution Amount",
      label: "Receipt Amount",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
      sortKey: "Contribution Amount (Sort)",
      format: "currency",
      render: (row) => {
        const val = row["Contribution Amount"];
        const num =
          typeof val === "number"
            ? val
            : parseFloat(val?.replace(/[^\d.-]/g, "")) || 0;
        return `$${num.toLocaleString()}`;
      },
    },
  ];

  const topColumns = [
    {
      key: "Name",
      label: "Full Name",
      style: { width: "250px" },
      render: (row) => {
        const filtered = linkedAllReceiptData.filter(
          (r) =>
            r["Full Name"]?.trim().toLowerCase() ===
            row.Name?.trim().toLowerCase(),
        );
        const donorChartData = getDonorChartData(filtered);

        const content = (
          <div className="p-3 text-xs text-gray-800">
            <div className="text-sm font-semibold mb-2 text-gray-900">
              {row.Name}'s Yearly Receipts
            </div>
            {donorChartData.length > 0 ? (
              <div className="h-[180px] w-[300px]">
                <Line
                  data={{
                    labels: donorChartData.map((d) => d.date),
                    datasets: [
                      {
                        label: "Receipt",
                        data: donorChartData.map((d) => d.total),
                        borderColor: extractHexFromTailwind(color),
                        backgroundColor: "rgba(34, 197, 94, 0.2)",
                        fill: "origin",
                        tension: 0.3,
                        pointRadius: 4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        ticks: { autoSkip: true, maxTicksLimit: 6 },
                      },
                      y: {
                        ticks: {
                          callback: (val) => `$${val.toLocaleString()}`,
                          beginAtZero: true,
                        },
                      },
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => `$${ctx.raw.toLocaleString()}`,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="text-gray-500 italic">No receipts found.</div>
            )}
          </div>
        );

        return (
          <div className="relative flex items-center min-h-[22px] pr-6">
            <span className="leading-tight">
              {row.DonorDocId ? (
                <Link
                  to={`/seec/donor/${row.DonorDocId}`}
                  className="text-blue-700 hover:text-blue-900 underline"
                >
                  {row.Name}
                </Link>
              ) : (
                row.Name
              )}
            </span>
            {donorChartData.length > 0 && (
              <div className="absolute right-1 top-1/2 -translate-y-1/2">
                <HoverPopover
                  content={content}
                  popoverClassName="text-gray-800"
                  maxWidth={520}
                >
                  <InformationCircleIcon
                    className="w-4 h-4 text-gray-500 hover:text-gray-800 cursor-pointer"
                    aria-label={`Show receipts for ${row.Name}`}
                  />
                </HoverPopover>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "First Receipt",
      label: "First Receipt",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
      sortKey: "First Receipt (Sort)",
    },
    {
      key: "Last Receipt",
      label: "Last Receipt",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
      sortKey: "Last Receipt (Sort)",
    },
    {
      key: "Total Receipts",
      label: "Total Receipts",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
      sortKey: "Total Receipts (Sort)",
      format: "currency",
    },
    {
      key: "Max Receipt",
      label: "Max Receipt",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
      sortKey: "Max Receipt (Sort)",
      render: (row) =>
        typeof row["Max Receipt"] === "number"
          ? `$${row["Max Receipt"].toLocaleString()}`
          : "—",
    },
    {
      key: "Average Receipt",
      label: "Average Receipt",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
      sortKey: "Average Receipt (Sort)",
      format: "currency",
    },
    {
      key: "Number of Receipts",
      label: "# of Receipts",
      align: "center",
      style: { width: "120px", textAlign: "center" },
      headerAlign: "center",
    },
  ];

  const chartContributors = buildContributorTotals(
    filterRowsByYearWindow(allReceiptData, chartYearWindow)
  ).slice(0, 25);
  const receiptStatsRows = filterRowsByYearWindow(
    allReceiptData,
    receiptStatsYearWindow
  );

  const topShareContributors = buildContributorTotals(
    filterRowsByYearWindow(allReceiptData, chartYearWindow)
  );
  const topShareLimit = Number(topShareCount) || 25;
  const topShareTotal = topShareContributors
    .slice(0, topShareLimit)
    .reduce((sum, entry) => sum + entry.rawTotal, 0);
  const overallShareTotal = topShareContributors.reduce(
    (sum, entry) => sum + entry.rawTotal,
    0
  );
  const everyoneElseTotal = Math.max(0, overallShareTotal - topShareTotal);

  const chartData = {
    labels: chartContributors.map((c) => c.Name),
    datasets: [
      {
        label: "Total Receipts",
        data: chartContributors.map((c) => c.rawTotal),
        backgroundColor: extractHexFromTailwind(color),
      },
    ],
  };
  const receiptAmounts = receiptStatsRows.map((row) =>
    Number(row["Contribution Amount (Sort)"] || 0)
  );
  const receiptStatsSummary = {
    maxReceipt: receiptAmounts.length ? Math.round(Math.max(...receiptAmounts)) : 0,
    averageReceipt: receiptAmounts.length
      ? Math.round(
          receiptAmounts.reduce((sum, amount) => sum + amount, 0) /
            receiptAmounts.length
        )
      : 0,
    minReceipt: receiptAmounts.length ? Math.round(Math.min(...receiptAmounts)) : 0,
  };
  const receiptStatsSeries = [
    {
      key: "receipt-amount",
      label: "Receipt Amount",
      data: [
        receiptStatsSummary.maxReceipt,
        receiptStatsSummary.averageReceipt,
        receiptStatsSummary.minReceipt,
      ],
      valueType: "currency",
      color: "#1d4ed8",
      defaultVisible: true,
    },
  ];

  const topSharePieItems = [
    {
      key: `top-${topShareLimit}`,
      label: `Top ${topShareLimit}`,
      value: topShareTotal,
      valueType: "currency",
      color: "#1e3a8a",
    },
    {
      key: "everyone-else",
      label: "Everyone Else",
      value: everyoneElseTotal,
      valueType: "currency",
      color: "#b91c1c",
    },
  ];

  const methodPaymentItems = Array.from(
    filterRowsByYearWindow(allReceiptData, methodYearWindow).reduce(
      (map, entry) => {
        const method = String(entry.Use || "Unknown").trim() || "Unknown";
        if (!map.has(method)) {
          map.set(method, {
            key: method.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
            label: method,
            value: 0,
            valueType: "currency",
            color: getChartColor(method.toLowerCase().replace(/[^a-z0-9]+/g, "-")),
          });
        }
        map.get(method).value += entry["Contribution Amount (Sort)"] || 0;
        return map;
      },
      new Map()
    ).values()
  ).sort((a, b) => b.value - a.value);
  const methodPaymentTotal = methodPaymentItems.reduce(
    (sum, entry) => sum + (entry.value || 0),
    0
  );
  const occupationItems = Array.from(
    filterRowsByYearWindow(allReceiptData, occupationYearWindow).reduce(
      (map, entry) => {
        const occupation = normalizeOccupationLabel(entry.Occupation);
        const key = occupation.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        if (!map.has(key)) {
          map.set(key, {
            key,
            label: occupation,
            value: 0,
            valueType: "currency",
            color: getChartColor(key),
          });
        }
        map.get(key).value += entry["Contribution Amount (Sort)"] || 0;
        return map;
      },
      new Map()
    ).values()
  ).sort((a, b) => b.value - a.value);
  const occupationTotal = occupationItems.reduce(
    (sum, entry) => sum + (entry.value || 0),
    0
  );
  const occupationDisplayItems =
    occupationItems.length > 8
      ? [
          ...occupationItems.slice(0, 7),
          {
            key: "other",
            label: "Other",
            value: occupationItems
              .slice(7)
              .reduce((sum, entry) => sum + (entry.value || 0), 0),
            valueType: "currency",
            color: "#6b7280",
          },
        ]
      : occupationItems;

  const chartOptions = {
    responsive: true,
    indexAxis: "y",
    interaction: {
      mode: "nearest",
      axis: "y",
      intersect: false,
    },
    scales: {
      x: {
        ticks: {
          callback: (val) => `$${Number(val).toLocaleString()}`,
        },
        title: {
          display: true,
          text: "Contribution Amount",
        },
      },
      y: {
        ticks: {
          autoSkip: false,
          font: { size: 12 },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx) => `$${ctx.raw.toLocaleString()}`,
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "y",
        },
        zoom: {
          wheel: {
            enabled: true,
            modifierKey: null,
          },
          pinch: {
            enabled: true,
          },
          mode: "y",
        },
      },
    },
  };
  return (
    <div className="mb-4 border border-gray-300 rounded">
      <button type="button" aria-expanded={!collapsed}
        className="w-full bg-gray-100 px-3 py-3 font-semibold text-lg text-gray-900 flex justify-between items-center text-left hover:bg-gray-200 rounded-none"
        onClick={toggle}>
        {title}
        <span aria-hidden="true">{collapsed ? "▸" : "▾"}</span>
      </button>

      {!collapsed && (
        <div className="bg-white p-4">
          <div
            className="text-md font-semibold mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showTop}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowTop((prev) => !prev)}
              >
              <span>Combined Receipt Sources</span>
              <span aria-hidden="true">{showTop ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={yearWindow}
                onChange={(e) => setYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showTop && (
            <CommonTable
              title=""
              columns={topColumns}
              data={linkedTopContributors}
              headerColor={color}
              showAggregateTotal={false}
            />
          )}

          {showRawTable && (
            <>
              <div
                className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
              >
                <button type="button" aria-expanded={showTable}
                  className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
                  onClick={() => setShowTable((prev) => !prev)}
                  >
                  <span>All Receipts</span>
                  <span aria-hidden="true">{showTable ? "▼" : "▶"}</span>
                </button>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">Number of Years</span>
                  <select
                    className="border rounded px-2 py-1 text-xs bg-white"
                    value={rawYearWindow}
                    onChange={(e) => setRawYearWindow(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {yearWindowOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {showTable && (
                <CommonTable
                  title=""
                  columns={rawColumns}
                  data={linkedRawData}
                  headerColor={color}
                  showAggregateTotal={false}
                  defaultSortField="__parsedDate"
                  defaultSortOrder="desc"
                />
              )}
            </>
          )}

          <div className="my-6 border-t border-gray-300" />

          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showChart}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowChart((prev) => !prev)}
              >
              <span>Top 25</span>
              <span aria-hidden="true">{showChart ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={chartYearWindow}
                onChange={(e) => setChartYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showChart && (
            <div className="w-full h-[750px]">
              <div className="w-full h-[750px]">
                <ShareableStaticGraph title="Contribution totals"><Bar data={chartData} options={chartOptions} /></ShareableStaticGraph>
              </div>
            </div>
          )}

          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showReceiptStatsChart}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowReceiptStatsChart((prev) => !prev)}
              >
              <span>Receipt Size Comparison</span>
              <span aria-hidden="true">{showReceiptStatsChart ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={receiptStatsYearWindow}
                onChange={(e) => setReceiptStatsYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showReceiptStatsChart && (
            <LineChartWithToggles
              labels={["Max Receipt", "Average Receipt", "Smallest Receipt"]}
              series={receiptStatsSeries}
              title={null}
              showTotalWhenNoneSelected={false}
              showSeriesToggles={false}
              showLegend={false}
              showChartTypeSelector={true}
              initialChartType="bar"
              defaultYAxisMode="zero"
              yAxisTitle="Receipt Amount"
            />
          )}

          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showTopShareChart}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowTopShareChart((prev) => !prev)}
              >
              <span>Top Share</span>
              <span aria-hidden="true">{showTopShareChart ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Top</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={topShareCount}
                onChange={(e) => setTopShareCount(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {topCountOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showTopShareChart && (
            <div className="w-full max-w-[900px]">
              <div className="mb-3 text-sm text-gray-700">
                <div className="font-semibold text-base text-gray-900 mb-1">
                  Impact of the Top {topShareLimit}
                </div>
                <div>
                  Top {topShareLimit} contributors gave{" "}
                  <span className="font-semibold">
                    {overallShareTotal > 0
                      ? ((topShareTotal / overallShareTotal) * 100).toFixed(1)
                      : "0.0"}
                    %
                  </span>{" "}
                  of total receipts in the selected year window. Total receipts:{" "}
                  <span className="font-semibold">
                    ${overallShareTotal.toLocaleString()}
                  </span>
                  .
                </div>
              </div>
              <PieChartWithToggles
                title={null}
                items={topSharePieItems}
                percentOf="all"
                showLegend={false}
                showSeriesToggles={false}
                showChartTypeSelector={false}
                aggregateOthers={false}
                includeZeroValues={true}
                showLegendTable={true}
                legendTablePlacement="right"
                legendTableMaxHeight="360px"
                enableHoverPop={true}
                hoverPopOffset={12}
                sliceOrder="input"
              />
            </div>
          )}

          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showMethodPieChart}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowMethodPieChart((prev) => !prev)}
              >
              <span>Method of Payment</span>
              <span aria-hidden="true">{showMethodPieChart ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={methodYearWindow}
                onChange={(e) => setMethodYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showMethodPieChart && (
            <div className="w-full max-w-[900px]">
              <div className="mb-3 text-sm text-gray-700">
                <div className="font-semibold text-base text-gray-900 mb-1">
                  Payment Method Breakdown
                </div>
                <div>
                  This chart shows how total receipts were paid in the selected
                  year window. Total receipts:{" "}
                  <span className="font-semibold">
                    ${methodPaymentTotal.toLocaleString()}
                  </span>
                  .
                </div>
              </div>
              <PieChartWithToggles
                title={null}
                items={methodPaymentItems}
                percentOf="all"
                showLegend={false}
                showSeriesToggles={false}
                showChartTypeSelector={false}
                aggregateOthers={true}
                maxSlices={8}
                includeZeroValues={true}
                showLegendTable={true}
                legendTablePlacement="right"
                legendTableMaxHeight="360px"
                enableHoverPop={true}
                hoverPopOffset={12}
                sliceOrder="valueDesc"
              />
            </div>
          )}

          <div
            className="text-md font-semibold mt-4 mb-2 flex justify-between items-center gap-4 flex-wrap"
          >
            <button type="button" aria-expanded={showOccupationPieChart}
              className="flex items-center gap-2 bg-transparent text-left text-gray-900 hover:text-blue-700"
              onClick={() => setShowOccupationPieChart((prev) => !prev)}
              >
              <span>Occupation</span>
              <span aria-hidden="true">{showOccupationPieChart ? "▼" : "▶"}</span>
            </button>
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-gray-700">Number of Years</span>
              <select
                className="border rounded px-2 py-1 text-xs bg-white"
                value={occupationYearWindow}
                onChange={(e) => setOccupationYearWindow(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              >
                {yearWindowOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showOccupationPieChart && (
            <div className="w-full max-w-[900px]">
              <div className="mb-3 text-sm text-gray-700">
                <div className="font-semibold text-base text-gray-900 mb-1">
                  Occupation Breakdown
                </div>
                <div>
                  This chart shows the largest occupation groups in the selected
                  year window, with smaller occupations grouped into Other.
                  Total receipts:{" "}
                  <span className="font-semibold">
                    ${occupationTotal.toLocaleString()}
                  </span>
                  .
                </div>
              </div>
              <PieChartWithToggles
                title={null}
                items={occupationDisplayItems}
                percentOf="all"
                showLegend={false}
                showSeriesToggles={false}
                showChartTypeSelector={false}
                initialChartType="doughnut"
                aggregateOthers={false}
                includeZeroValues={true}
                showLegendTable={true}
                legendTablePlacement="right"
                legendTableMaxHeight="360px"
                enableHoverPop={true}
                hoverPopOffset={12}
                sliceOrder="valueDesc"
              />
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default UnifiedContributionSection;
